import React, { type ReactNode } from 'react';
import type { HoldingsCustodyIssue, OperationStep } from '@/types/operations';
import styles from './OperationsChecklist.module.css';
import shared from '@/components/utils/shared.module.css';

type StepDetailRenderer = (step: OperationStep) => ReactNode;

// ── Registered renderer: holdings_custody_availability ───────────────────────
// result_detail.issues = [{ client_id, client_code, fa_account_no, missing: string[] }]
const holdingsCustodyRenderer: StepDetailRenderer = (step) => {
  const detail = step.result_detail ?? {};

  if (typeof detail.error === 'string') {
    return (
      <p className={styles['step-detail-error']}>
        The check could not be evaluated ({detail.error}). Try re-running it.
      </p>
    );
  }

  if (!Array.isArray(detail.issues)) {
    return (
      <p className={styles['step-detail-empty']}>
        {step.summary || 'This check has not produced any detail yet.'}
      </p>
    );
  }

  const issues = detail.issues as HoldingsCustodyIssue[];
  if (issues.length === 0) {
    return <p className={styles['step-detail-empty']}>No missing holdings or custody data.</p>;
  }

  return (
    <table className={shared.table}>
      <thead>
        <tr>
          <th>Client Code</th>
          <th>FA Account No</th>
          <th>Missing Data</th>
        </tr>
      </thead>
      <tbody>
        {issues.map((issue, index) => (
          <tr key={issue.client_id ?? issue.client_code ?? index}>
            <td>{issue.client_code}</td>
            <td>{issue.fa_account_no}</td>
            <td>
              {(issue.missing ?? []).map((dataType) => (
                <span key={dataType} className={styles['missing-badge']}>
                  {dataType}
                </span>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── Generic fallback ─────────────────────────────────────────────────────────
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const unionKeys = (rows: Record<string, unknown>[]): string[] => {
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) { seen.add(key); ordered.push(key); }
    }
  }
  return ordered;
};

const genericRenderer: StepDetailRenderer = (step) => {
  const detail = step.result_detail ?? {};

  if (typeof detail.error === 'string') {
    return (
      <p className={styles['step-detail-error']}>
        The check could not be evaluated ({detail.error}). Try re-running it.
      </p>
    );
  }

  const issues = detail.issues;
  if (Array.isArray(issues) && issues.length > 0 && issues.every(isPlainObject)) {
    const rows = issues as Record<string, unknown>[];
    const columns = unionKeys(rows);
    // A column whose populated values are all numbers is right-aligned, header and cells alike.
    const numericCols = new Set(columns.filter((col) => {
      const values = rows.map((row) => row[col]).filter((v) => v !== null && v !== undefined);
      return values.length > 0 && values.every((v) => typeof v === 'number');
    }));
    const alignClass = (col: string) => (numericCols.has(col) ? shared['text-right'] : undefined);
    return (
      <table className={shared.table}>
        <thead>
          <tr>
            {columns.map((col) => <th key={col} className={alignClass(col)}>{col}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const id = row['id'] ?? row['client_id'];
            const key = (typeof id === 'string' || typeof id === 'number') ? id : i;
            return (
              <tr key={key}>
                {columns.map((col) => <td key={col} className={alignClass(col)}>{formatCell(row[col])}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <p className={styles['step-detail-empty']}>
      {step.summary || 'No additional detail.'}
    </p>
  );
};

// Registry keyed by step_key. Add an entry here when a new step ships a bespoke
// result_detail shape; everything else falls through to the generic renderer.
const STEP_DETAIL_RENDERERS: Record<string, StepDetailRenderer> = {
  holdings_custody_availability: holdingsCustodyRenderer,
};

export const renderStepDetail = (step: OperationStep): ReactNode => {
  const renderer = STEP_DETAIL_RENDERERS[step.step_key] ?? genericRenderer;
  return renderer(step);
};
